import { Component, Input, NgZone, OnInit, ViewChild, inject } from '@angular/core';
import { ApiContribution, ApiMembership, ApiOffer, ApiOfferSet, ApiPaymentSource, ApiUser, ContributionsApi, UsersApi } from '@tytapp/api';
import { FormComponent, FormHandlingComponent, FormInput, PageComponent, Redirection } from '@tytapp/common';
import { environment } from '@tytapp/environments/environment';
import { CreateStripeTokenResponse, StripeService } from '@tytapp/stripe';
import { UserService } from '@tytapp/user';
import { CVCValidator, CreditCardValidator, ExpirationDateValidator, NonEmptyValidator, Validated } from '@tytapp/validation';
import { SubSink } from 'subsink';
import { LocalStorageService } from 'angular-2-local-storage'; // <--- Import LocalStorageService

export interface PaymentSession {
  token: string;
  complete(success: boolean): Promise<void>;
}

@Component({
  selector: 'tyt-payment-offers-step',
  templateUrl: './payment-offers-step.component.html',
  styleUrls: ['./payment-offers-step.component.scss']
})
export class PaymentOffersStepComponent extends PageComponent implements FormHandlingComponent, OnInit {
  private userService = inject(UserService);
  private stripe = inject(StripeService);
  private userApi = inject(UsersApi);
  private ngZone = inject(NgZone);
  private redirection = inject(Redirection);
  private contributionsApi = inject(ContributionsApi);
  private localStorage = inject(LocalStorageService);
  private _offerSet: ApiOfferSet;

  @Input()
  get offerSet() {
    return this._offerSet;
  }

  set offerSet(value) {
    this._offerSet = value;
    setTimeout(() => {
      if (this.userLoaded)
        this.validateUserState();
    }, 100);
  }

  @Input()
  offer: ApiOffer;

  @Input()
  offerUUID: string;

  @Input()
  offerSetUUID: string;

  @Validated({ class: NonEmptyValidator, field: 'cardholder-name' })
  @FormInput()
  cardholderName: string;

  @Validated({ class: CreditCardValidator, field: 'cc-number' })
  @FormInput()
  cardNumber: string;

  @Validated({ class: ExpirationDateValidator, field: 'cc-exp' })
  @FormInput()
  expiryDate: string;


  @Validated({ class: CVCValidator, field: 'cc-csc' })
  @FormInput()
  cvc: string;

  @FormInput()
  zip: string;

  user: ApiUser;

  private subsink = new SubSink();
  clientLoaded = false;
  applePayAvailable = false;
  userLoaded = false;
  submitting = false;
  @Input()
  price: number;
  @Input()
  period: string;

  enterNewCard = false;

  get inDev() {
    return !environment.production;
  }

  get recurring() {
    return this.applicablePeriod !== 'one_time';
  }

  paymentSource: ApiPaymentSource;

  async init() {
    this.shell.title = 'Payment';
    this.subsink.add(this.userService.userChanged.subscribe(async user => {
      this.user = user;
      this.userLoaded = true;
      this.validateUserState();
      try {
        this.paymentSource = await this.userApi.getPaymentSource().toPromise();
      } catch (e) {
        console.error(`Could not retrieve user's current payment source: ${e.message || e.error}`);
        console.error(e);
      }
    }));

    // If we are on the client side, check if Apple Pay is available on this 
    // browser.

    if (typeof window !== 'undefined') {
      this.clientLoaded = true;
      await this.stripe.apiReady();
      this.stripe.applePay.checkAvailability(
        available => {
          console.log(`Apple Pay availability: ${available ? 'Available' : 'Not Available'}`);
          this.applePayAvailable = available;
        }
      );
    }

    this.userService.sendBeacons('Begin Checkout', () => {
      this.userService.sendBeaconGA4('begin_checkout', {
        currency: 'USD',
        value: this.offer.dollar_price,
        items: [ 
          {
            item_id: this.offer.period === 'one_time' ? 'contribution' : (this.offer.membership_product?.product_sku ?? 'unknown'),
            item_name: this.offer.period === 'one_time' ? 'Contribution' : (this.offer.membership_product?.name ?? 'Unknown'),
            item_brand: 'TYT',
            item_category: this.offer.period === 'one_time' ? 'Contribution' : 'Membership',
            price: this.offer.dollar_price,
            item_list_id: this.offerSet.uuid,
            item_list_name: this.offerSet.name,
            quantity: 1
          }
        ]
      });
    });
  }

  @Input()
  inSettings: boolean = false;

  justPurchased = false;

  validateUserState() {
    if (this.justPurchased)
      return;

    // Do not do any redirection if we are in the One Time Donations area of the Settings UI.

    if (this.inSettings)
      return;

    // Do not do any redirection if we haven't loaded the offer set yet

    if (!this.offerSet)
      return;

    // Redirect back to the pricing page if the URL contains an offer that is not present in this offer set 
    // (ignoring the special pay-what-you-want "p:*" IDs)

    if (this.offerUUID && !this.offerUUID.startsWith('p:') && !this.offerSet.offers.some(x => x.uuid === this.offerUUID)) {
      this.redirection.go(`/join/m/${this.offerSetUUID}/x/pricing`);
      return;
    }

    // If the user is not signed in, send them to the signup step

    if (!this.user) {
      this.navigateBackToSignup();
      return;
    }

    // If this is a recurring (ie membership) offer, and the user has membership, redirect them to the Change Membership
    // page.

    if (this.recurring && this.user && this.user.membership && this.user.membership.active && this.user.membership.gateway_name != 'free') {
      this.redirection.go(`/membership/change`);
      return;
    }
  }

  private get hasOneTimeOffers() {
    if (!this.offerSet || !this.offerSet.offers)
      return false;

    return this.offerSet.offers.some(x => x.period === 'one_time');
  }

  navigateBackToSignup() {
    this.redirection.go(`/join/m/${this.offerSet.uuid}/${this.offerUUID}/signup`);
  }

  async destroy() {
    this.subsink.unsubscribe();
  }

  async submitApplePay() {
    if (this.submitting) return;
    this.processPayment('apple-pay');
  }

  async submit() {
    if (this.submitting) return;
    this.processPayment('credit-card');
  }

  useNewCard() {
    this.enterNewCard = true;
    this.hideError();
  }

  useExistingCard() {
    this.enterNewCard = false;
    this.hideError();
  }

  @ViewChild('form')
  form: FormComponent;

  async processPayment(method: 'credit-card' | 'apple-pay') {
    this.submitting = true;
    this.error = false;

    let session: PaymentSession;

    if (method === 'credit-card') {

      if (!this.paymentSource || this.enterNewCard) {
        if (!this.form.validate()) {
          this.submitting = false;
          return;
        }
      }

      try {
        session = await this.acquireCreditCardToken();
      } catch (e) {
        this.showError(e.message || e.error);
        setTimeout(() => this.submitting = false, 3000);
        return;
      }
    } else if (method === 'apple-pay') {
      try {
        session = await this.acquireApplePayToken();
      } catch (e) {
        this.showError(e.message || e.error);
        setTimeout(() => this.submitting = false, 3000);
        return;
      }

      if (!session) {
        this.submitting = false;
      }
    } else {
      alert(`Unknown payment method ${method}`);
      return;
    }

    let purchase: ApiMembership | ApiContribution;
    let purchaseType: 'Membership' | 'Contribution';

    try {
      if (this.period !== 'one_time') {
        purchaseType = 'Membership';
        purchase = await this.createMembership(session.token, method);
      } else {
        purchaseType = 'Contribution';
        purchase = await this.createContribution(session.token);
      }

      console.info(`Completing active payment session`);
      await session.complete(true);
    } catch (e) {
      if (e.json)
        e = e.json();

      if (this.period !== 'one_time') {
        console.error('Caught exception while creating membership');
      } else {
        console.error('Caught exception while creating contribution');
      }

      console.error(e);

      console.info(`Cancelling active payment session`);
      await session.complete(false);

      this.showError(e.message || e.error);
      setTimeout(() => this.submitting = false, 3000);
      return;
    }

    this.navigateToConfirmation(purchaseType, purchase);
  }

  navigateToConfirmation(type: 'Membership' | 'Contribution', purchase: ApiMembership | ApiContribution) {
    //save latest purchase ID and type to local storage

    const queryParams = new URLSearchParams({
      pt: type,
      pu: purchase.uuid
    });

    if (type === 'Membership') {
      let membership = purchase as ApiMembership;
      queryParams.set('mp', String(membership?.product?.product_sku ?? membership?.product?.id ?? 'none'));
    } else if (type === 'Contribution') {
      let contribution = purchase as ApiContribution;
      queryParams.set('cg', String(contribution.entitlement_periods || 0));
    }

      this.router.navigateByUrl(
        `/join/m/${this.offerSetUUID}/${this.offerUUID}/confirmed?${queryParams.toString()}`
      );
  }

  async acquireApplePayToken(): Promise<PaymentSession> {
    if (typeof window === 'undefined') {
      console.error('Cannot do Apple Pay purchase on the server-side!');
      return;
    }

    return await new Promise<PaymentSession>((resolve, reject) => {
      this.ngZone.runOutsideAngular(() => {
        let session = this.stripe.applePay.buildSession({
          total: {
            label: this.offer ? this.offer.title : 'TYT Membership',
            amount: this.offer ? this.offer.dollar_price : this.price / 100
          },
          countryCode: 'US',
          currencyCode: 'USD'
        },
          (result, completion) =>
            this.ngZone.run(() =>
              setTimeout(() =>
                resolve(<PaymentSession>{
                  complete: async (success: boolean) => await completion(success),
                  token: result.token.id
                })
              )
            ),
          (e) => {
            console.error('Error caught during Apple Pay buildSession():');
            console.error(e);
            reject(e);
          });

        session.oncancel = () =>
          this.ngZone.run(() =>
            setTimeout(() =>
              resolve(null)
            )
          )
          ;

        session.begin();
      });
    });
  }

  async acquireCreditCardToken(): Promise<PaymentSession> {
    if (this.paymentSource && !this.enterNewCard) {
      return {
        complete: async (success: boolean) => { },
        token: this.paymentSource.card_id
      }
    }

    let token: CreateStripeTokenResponse;
    token = await this.stripe.createToken({
      name: this.cardholderName,
      number: this.cardNumber,
      exp: this.expiryDate,
      cvc: this.cvc,
      address_zip: this.zip
    });

    if (token.response.error && token.response.error.message)
      throw new Error(token.response.error.message);

    return {
      complete: async (success: boolean) => { },
      token: token.response.id
    };
  }

  async createContribution(token: string): Promise<ApiContribution> {
    let contribution = await this.contributionsApi.makeContribution({
      email: this.user.email,
      gateway_name: 'stripe',
      offer_set_uuid: this.offerSet?.uuid,
      offer_uuid: this.offer?.uuid,
      payment_info: <any>{ token },
      amount: this.applicablePrice
    }).toPromise();

    await this.recordContributionPurchaseEvent(contribution);

    return contribution;
  }

  async createMembership(token: string, paymentType: string) {
    let membership: ApiMembership;
    let annual = ['anually', 'annually'].includes(this.offer ? this.offer.period : this.period);

    this.userService.sendBeacons('Add Payment Information', () => {
      this.userService.sendBeaconGA4('add_payment_info', {
        currency: 'USD',
        value: this.offer.dollar_price,
        payment_type: (paymentType || 'unknown').replace(/-/g, '_'),
        items: this.offerSet.offers.map(offer => ({
          item_id: offer.membership_product.product_sku,
          item_name: offer.membership_product.name,
          item_brand: 'TYT',
          item_category: 'Membership',
          price: offer.dollar_price,
          item_list_id: this.offerSet.uuid,
          item_list_name: this.offerSet.name,
          quantity: 1
        }))
      });
    });

    membership = await this.userApi.createMembership({
      offer_set_uuid: this.offerSet ? this.offerSet.uuid : undefined,
      price: this.price ? this.price : undefined,
      period: this.period ? this.period : undefined,
      offer_uuid: this.offer ? this.offer.uuid : null,
      annual,
      gateway_name: 'stripe',
      gateway_data: <any>{ token },
      source: this.userService.source,
      source_first_time: this.userService.sourceFirstTime
    }).toPromise();

    this.justPurchased = true;
    this.user.membership = membership;
    this.userService.user = this.user;

    try {
      await this.recordMembershipPurchaseEvent(membership);
    } catch (e) {
      if (environment.debug) {
        alert(`Preproduction Alert: Caught error while recording purchase event. Please open the Console and send details to Engineering.`);
      }

      console.error(`Caught error while recording purchase event:`);
      console.error(e);
    }

    return membership;
  }

  async recordMembershipPurchaseEvent(membership: ApiMembership) {
    let beaconsEnabled = environment.settings.enableBeacons || window['force_beacons'] === 1;
    if (!beaconsEnabled) {
      console.log(`Skipping membership purchase event beacons [disabled for environment]...`);
      return;
    }
    console.log(`Sending membership purchase event beacons...`);

    let offerSetUUID = this.offerSetUUID;
    let offerUUID = this.offerUUID;

    if (this.offerSet.is_pay_what_you_want && !offerUUID) {
      offerUUID = `p:${this.applicablePrice}:${this.applicablePeriod}`;
    }

    this.userService.sendBeacons('Membership purchase', async () => {
      this.userService.sendBeaconGA4('purchase', {
        purchase_type: 'membership',
        offer_presentation: this.inSettings ? 'settings' : this.offer.kind === 'embedded' ? 'embedded' : 'page',
        app: 'tyt_accounts',
        path: 'offers_v2',
        offers_version: 2,
        offer_set: this.offerSetUUID,
        offer: this.offerUUID,
        membership_product_id: membership.product.id,
        membership_product_sku: membership.product.product_sku,
        transaction_id: membership.uuid,
        currency: 'USD',
        value: this.applicablePriceUSD,
        items: [{
          item_id: membership.product.product_sku,
          item_name: membership.product.name,
          item_brand: 'TYT',
          item_category: 'Membership',
          price: this.applicablePriceUSD,
          quantity: 1
        }]
      });

      // Let our Facebook Pixel(s) know...
      this.userService.sendBeaconFB(
        'Purchase',
        'tytaccounts_membership_purchased',
        {
          value: this.applicablePriceUSD,
          currency: 'USD',
          contents: [
            {
              id: membership.product ? membership.product.id : 0,
              quantity: 1
            }
          ],
          purchase_type: 'membership',
          offer_set_id: this.offerSetUUID,
          offer_id: this.offerUUID,
          membership_product_sku: membership.product ? membership.product.product_sku : 'unknown',
          membership_product_id: membership.product ? membership.product.id : 0,
          content_type: 'product'
        }
      );

      // Make sure enough time is allowed for the beacons to go out.
      console.log(`Allowing purchase event beacons to complete...`);
      await this.timeout(3000);
      console.log(`Purchase beacons completed.`);
    });

  }

  async recordContributionPurchaseEvent(contribution: ApiContribution) {
    let beaconsEnabled = environment.settings.enableBeacons || window['force_beacons'] === 1;
    if (!beaconsEnabled) {
      console.log(`Skipping contribution purchase event beacons [disabled for environment]...`);
      return;
    }

    console.log(`Sending contribution purchase event beacons...`);
    this.userService.sendBeacons('Contribution purchase', async () => {
      this.userService.sendBeaconGA4('purchase', {
        purchase_type: 'contribution',
        offer_presentation: this.inSettings ? 'settings' : this.offer.kind === 'embedded' ? 'embedded' : 'page',
        app: 'tyt_accounts',
        path: 'offers_v2',
        offers_version: 2,
        offer_set: this.offerSetUUID,
        offer: this.offerUUID,
        entitlement_granted: contribution.entitlement_periods > 0,
        entitlement_periods: contribution.entitlement_periods,
        transaction_id: contribution.uuid,
        currency: 'USD',
        value: contribution.amount / 100.0,
        items: [{
          item_id: 'contribution',
          item_name: 'Contribution to TYT',
          item_brand: 'TYT',
          item_category: 'Contribution',
          price: contribution.amount / 100.0,
          quantity: 1
        }]
      });

      // Let our Facebook Pixel(s) know...
      this.userService.sendBeaconFB(
        'Purchase',
        'tytaccounts_contribution_purchased',
        {
          value: this.applicablePriceUSD,
          currency: 'USD',
          contents: [
            {
              id: `otc_${this.applicablePrice}`,
              quantity: 1
            }
          ],
          purchase_type: 'contribution',
          offer_set_id: this.offerSetUUID,
          offer_id: this.offerUUID,
          contribution_product_sku: `One Time Contribution ($${this.applicablePriceUSD})`,
          contribution_product_id: `otc_${this.applicablePrice}`,
          content_type: 'product'
        }
      );

      // Make sure enough time is allowed for the beacons to go out.
      console.log(`Contribution: Allowing purchase event beacons to complete...`);
      await this.timeout(3000);
      console.log(`Contribution: Purchase beacons completed.`);

    });

  }

  private timeout(time = 0) {
    return new Promise<void>(resolve => setTimeout(() => resolve(), time));
  }

  get applicablePeriod() {
    return this.offer ? this.offer.period : this.period;
  }

  get perApplicablePeriod() {
    return ({
      monthly: '/month',
      annually: '/year',
      one_time: ''
    })[this.applicablePeriod] || '';
  }

  applicablePeriodPlural(count: number) {
    if (this.applicablePeriod === 'monthly')
      return count === 1 ? 'month' : 'months';
    else (this.applicablePeriod === 'annually')
    return count === 1 ? 'year' : 'years';
  }

  get applicablePrice() {
    if (this.offer)
      return this.offer.price;

    return this.price;
  }

  get applicablePriceUSD() {
    return this.applicablePrice / 100;
  }

  get applicableIntroductoryPeriods() {
    if (!this.offerSet)
      return 0;

    if (!this.offerSet.is_pay_what_you_want) {
      // FIXED PRICING
      if (this.offer)
        return this.offer.introductory_periods;
      else
        return 0;
    } else {
      // PWYW
      if (this.applicablePeriod === 'monthly') {
        return this.offerSet.pwyw_monthly_introductory_periods;
      } else if (this.applicablePeriod === 'annually') {
        return this.offerSet.pwyw_annual_introductory_periods;
      }

      return 0;
    }
  }

  get applicableProduct() {
    if (!this.offerSet)
      return null;

    if (!this.offerSet.is_pay_what_you_want) {
      // FIXED PRICING
      if (this.offer)
        return this.offer.membership_product;
      else
        return null;
    } else {
      // PWYW
      if (this.applicablePeriod === 'monthly') {
        return this.offerSet.pwyw_monthly_introductory_product;
      } else if (this.applicablePeriod === 'annually') {
        return this.offerSet.pwyw_annual_introductory_product;
      }

      return null;
    }
  }

  get applicableProductPrice() {
    if (!this.applicableProduct)
      return 0;

    return this.applicableProduct.price;
  }

  get applicableProductPriceUSD() {
    return this.applicableProductPrice / 100;
  }

  get applicableTrialDays() {
    if (!this.offerSet || this.offerSet.is_pay_what_you_want)
      return 0;

    if (this.offer)
      return this.offer.trial_days;

    return 0;
  }
}